import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Trash2, Info } from 'lucide-react';
import { useToast } from '../../components/common/ToastContainer';
import configTemplateService from '../../services/configTemplate.service';
import { useTenantStore } from '../../stores/tenantStore';
import { usePermissions } from '../../hooks/usePermissions';

interface PhysicalPortConfig {
  port_name: string;
  port_enabled: boolean;
  port_type: number;
  mtu: number;
  description: string;
}

interface LogicalPortConfig {
  logical_name: string;
  description: string;
  role: string;
  type: string;
  port_id: string;
  vlan_id?: number;
  dhcp_client: boolean;
  ipv4: string;
  gateway: string;
  mtu: number;
  mss: string;
  subscriptions: string;
  vrf_id?: string;
}

interface StaticRouteConfig {
  ip: string;
  target_ip: string;
  ip_mask: string;
  next_hop_ip: string;
  next_hop_port_id: string;
  metric: number;
  distance: number;
  description: string;
  enabled: boolean;
}

interface SnatConfig {
  name: string;
  type: string;
  in_ip: string;
  out_ip: string;
  out_interface_name: string;
  description: string;
}

interface DnatConfig {
  name: string;
  protocol: string;
  out_ip: string;
  out_port: string;
  local_ip: string;
  local_port: string;
  out_interface: string;
  description: string;
}



interface Wg0Config {
  enabled: boolean;
  listen_port: number;
  address: string;
  mtu: number;
}

interface Wg1Config {
  enabled: boolean;
  tunnel_protocol: 'wireguard' | 'ikev2';
  listen_port: number;
  address: string;
  dns: string;
  mtu: number;
}

interface FlowprobeTemplateConfig {
  enabled: boolean;
  collector_address: string;
  collector_port: number;
  template_interval: number;
  active_timer: number;
  passive_timer: number;
  record_l2: boolean;
  record_l3: boolean;
  record_l4: boolean;
}

interface DhcpPoolConfig {
  name: string;
  interface_name: string;
  start_ip: string;
  end_ip: string;
  gateway: string;
  dns_servers: string;
  lease_time: number;
  enabled: boolean;
}

const ConfigTemplateDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { canUpdate } = usePermissions();
  const { selectedTenantId } = useTenantStore();
  const isEditMode = id && id !== 'create';

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form State
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [edgeModel, setEdgeModel] = useState('VSR1000');

  // Config State
  const [physicalPorts, setPhysicalPorts] = useState<PhysicalPortConfig[]>([]);
  const [logicalPorts, setLogicalPorts] = useState<LogicalPortConfig[]>([]);
  const [staticRoutes, setStaticRoutes] = useState<StaticRouteConfig[]>([]);
  const [snatRules, setSnatRules] = useState<SnatConfig[]>([]);
  const [dnatRules, setDnatRules] = useState<DnatConfig[]>([]);
  const [wg0Config, setWg0Config] = useState<Wg0Config>({
    enabled: true, listen_port: 51820, address: '10.200.0.1/24', mtu: 1420,
  });
  const [wg1Config, setWg1Config] = useState<Wg1Config>({
    enabled: true, tunnel_protocol: 'wireguard', listen_port: 51821,
    address: '10.10.0.254/16', dns: '8.8.8.8', mtu: 1420,
  });
  const [sshTunnel, setSshTunnel] = useState({ enabled: true });
  const [flowprobeConfig, setFlowprobeConfig] = useState<FlowprobeTemplateConfig>({
    enabled: true, collector_address: '169.254.1.2', collector_port: 4739,
    template_interval: 20, active_timer: 60, passive_timer: 120,
    record_l2: false, record_l3: true, record_l4: true,
  });
  const [dhcpPools, setDhcpPools] = useState<DhcpPoolConfig[]>([]);

  // Default Physical Ports for VSR1000
  const defaultPhysicalPorts: PhysicalPortConfig[] = [
    { port_name: 'G0', port_enabled: true, port_type: 257, mtu: 1500, description: 'WAN Interface' },
    { port_name: 'G1', port_enabled: true, port_type: 257, mtu: 1500, description: 'LAN Interface' },
    { port_name: 'G2', port_enabled: false, port_type: 257, mtu: 1500, description: '' },
    { port_name: 'G3', port_enabled: false, port_type: 257, mtu: 1500, description: '' },
    { port_name: 'G4', port_enabled: false, port_type: 257, mtu: 1500, description: '' },
    { port_name: 'G5', port_enabled: false, port_type: 257, mtu: 1500, description: '' },
    { port_name: 'G6', port_enabled: false, port_type: 257, mtu: 1500, description: '' },
  ];

  useEffect(() => {
    if (isEditMode) {
      loadTemplate(id!);
    } else {
      setPhysicalPorts(defaultPhysicalPorts);
    }
  }, [id, isEditMode]);

  const loadTemplate = async (templateId: string) => {
    try {
      setLoading(true);
      const template = await configTemplateService.getById(templateId);
      setName(template.name);
      setDescription(template.description || '');
      setIsDefault(!!template.is_default);
      setEdgeModel(template.edge_model);

      if (template.config) {
        setPhysicalPorts(template.config.physical_ports || []);
        setLogicalPorts(template.config.logical_ports || []);
        setStaticRoutes(template.config.static_routes || []);
        setSnatRules(template.config.snat || []);
        setDnatRules(template.config.dnat || []);
        setDhcpPools(template.config.dhcp_pools || []);

        // New-format wg0/wg1
        if (template.config.wg0) {
          setWg0Config(template.config.wg0);
        } else if (template.config.wireguard && !template.config.wg0) {
          // Legacy wireguard → wg0 migration
          const wg = template.config.wireguard;
          setWg0Config({
            enabled: wg.enabled || false,
            listen_port: wg.listen_port || 51820,
            address: wg.address || '10.200.0.1/24',
            mtu: wg.mtu || 1420,
          });
        }

        if (template.config.wg1) {
          setWg1Config(template.config.wg1);
        }

        if (template.config.ssh_tunnel) {
          setSshTunnel(template.config.ssh_tunnel);
        }

        if (template.config.flowprobe) {
          setFlowprobeConfig(template.config.flowprobe);
        }
      }
    } catch (error: any) {
      console.error('Failed to load template:', error);
      toast.error('Load Failed', error.message || 'Failed to load template');
      navigate('/admin/config-templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error('Validation Error', 'Template name is required');
      return;
    }

    try {
      setSaving(true);
      const config = {
        physical_ports: physicalPorts,
        logical_ports: logicalPorts,
        static_routes: staticRoutes,
        snat: snatRules,
        dnat: dnatRules,
        wg0: wg0Config,
        wg1: wg1Config,
        ssh_tunnel: sshTunnel,
        flowprobe: flowprobeConfig,
        dhcp_pools: dhcpPools,
      };

      if (isEditMode) {
        await configTemplateService.update(id!, { name, description, is_default: isDefault, config });
        toast.success('Template Updated', 'Configuration template has been updated');
      } else {
        await configTemplateService.create({ name, description, is_default: isDefault, edge_model: edgeModel, config, tenant_id: selectedTenantId || undefined });
        toast.success('Template Created', 'Configuration template has been created');
      }
      navigate('/admin/config-templates');
    } catch (error: any) {
      console.error('Failed to save template:', error);
      toast.error('Save Failed', error.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const updateList = <T,>(list: T[], index: number, field: keyof T, value: any, setList: (l: T[]) => void) => {
    const newList = [...list];
    newList[index] = { ...newList[index], [field]: value };
    setList(newList);
  };

  const removeListItem = <T,>(list: T[], index: number, setList: (l: T[]) => void) => {
    const newList = [...list];
    newList.splice(index, 1);
    setList(newList);
  };

  // --- Handlers ---

  const addLogicalPort = () => {
    setLogicalPorts([...logicalPorts, {
      logical_name: `logical${logicalPorts.length + 1}`,
      description: '',
      role: 'LAN',
      type: 'Routed',
      port_id: physicalPorts[0]?.port_name || '',
      vlan_id: undefined,
      dhcp_client: false,
      ipv4: '',
      gateway: '',
      mtu: 1500,
      mss: '',
      subscriptions: '',
    }]);
  };

  const addStaticRoute = () => {
    setStaticRoutes([...staticRoutes, {
      ip: '',
      target_ip: '',
      ip_mask: '24',
      next_hop_ip: '',
      next_hop_port_id: '',
      metric: 1,
      distance: 1,
      description: '',
      enabled: true,
    }]);
  };

  const addSnatRule = () => {
    setSnatRules([...snatRules, {
      name: `snat${snatRules.length + 1}`,
      type: 'Masquerading',
      in_ip: '',
      out_ip: '',
      out_interface_name: '',
      description: '',
    }]);
  };

  const addDnatRule = () => {
    setDnatRules([...dnatRules, {
      name: `dnat${dnatRules.length + 1}`,
      protocol: 'TCP',
      out_ip: '',
      out_port: '',
      local_ip: '',
      local_port: '',
      out_interface: '',
      description: '',
    }]);
  };

  const addDhcpPool = () => {
    setDhcpPools([...dhcpPools, {
      name: `dhcp-pool${dhcpPools.length + 1}`,
      interface_name: logicalPorts[0]?.logical_name || '',
      start_ip: '',
      end_ip: '',
      gateway: '',
      dns_servers: '8.8.8.8,8.8.4.4',
      lease_time: 86400,
      enabled: true,
    }]);
  };

  if (loading) return <div className="p-8 text-center">Loading template...</div>;

  return (
    <div className="max-w-5xl mx-auto space-y-6 pb-20">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button onClick={() => navigate('/admin/config-templates')} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
            <ArrowLeft className="w-5 h-5 text-gray-500" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{isEditMode ? 'Edit Configuration Template' : 'Create Configuration Template'}</h1>
            <p className="text-sm text-gray-500">{isEditMode ? `Editing template: ${name}` : 'Define a new configuration template'}</p>
          </div>
        </div>
        {canUpdate && (
        <button onClick={handleSubmit} disabled={saving} className="btn-primary inline-flex items-center space-x-2">
          {saving ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> : <Save className="w-4 h-4" />}
          <span>Save Template</span>
        </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">

        {/* Basic Info */}
        <section className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Basic Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Template Name *</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="input-base w-full" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Edge Model</label>
              <select value={edgeModel} onChange={(e) => setEdgeModel(e.target.value)} className="input-base w-full" disabled>
                <option value="VSR1000">VSR1000</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="input-base w-full" />
            </div>
            <div className="flex items-center">
              <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
              <label className="ml-2 block text-sm text-gray-900">Set as Default Template for {edgeModel}</label>
            </div>
          </div>
        </section>

        {/* Physical Ports */}
        <section className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Physical Interfaces</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Port</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Enabled</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">MTU</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {physicalPorts.map((port, index) => (
                  <tr key={port.port_name}>
                    <td className="px-3 py-2 text-sm font-medium text-gray-900">{port.port_name}</td>
                    <td className="px-3 py-2">
                      <select
                        value={port.port_type || 257}
                        onChange={(e) => updateList(physicalPorts, index, 'port_type', parseInt(e.target.value), setPhysicalPorts)}
                        className="input-base w-full text-sm py-1"
                      >
                        <option value={257}>Ethernet</option>
                        <option value={768}>WiFi</option>
                        <option value={1024}>LTE</option>
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <input type="checkbox" checked={port.port_enabled} onChange={(e) => updateList(physicalPorts, index, 'port_enabled', e.target.checked, setPhysicalPorts)} className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
                    </td>
                    <td className="px-3 py-2">
                      <input type="number" value={port.mtu || 1500} onChange={(e) => updateList(physicalPorts, index, 'mtu', parseInt(e.target.value) || 1500, setPhysicalPorts)} className="input-base w-20 text-sm py-1" />
                    </td>
                    <td className="px-3 py-2">
                      <input type="text" value={port.description || ''} onChange={(e) => updateList(physicalPorts, index, 'description', e.target.value, setPhysicalPorts)} className="input-base w-full text-sm py-1" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Logical Ports */}
        <section className="card">
          <div className="flex items-center justify-between mb-4 border-b pb-2">
            <h2 className="text-lg font-semibold text-gray-900">Logical Interfaces</h2>
            <button type="button" onClick={addLogicalPort} className="text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center">
              <Plus className="w-4 h-4 mr-1" /> Add Interface
            </button>
          </div>
          <div className="space-y-4">
            {logicalPorts.length === 0 && <p className="text-gray-500 text-sm italic text-center">No logical interfaces defined.</p>}
            {logicalPorts.map((port, index) => (
              <div key={index} className="border border-gray-200 rounded-md p-4 bg-gray-50 relative">
                <button type="button" onClick={() => removeListItem(logicalPorts, index, setLogicalPorts)} className="absolute top-2 right-2 text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Name *</label>
                    <input type="text" value={port.logical_name} onChange={(e) => updateList(logicalPorts, index, 'logical_name', e.target.value, setLogicalPorts)} className="input-base w-full text-sm py-1" required />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Type *</label>
                    <select value={port.type} onChange={(e) => updateList(logicalPorts, index, 'type', e.target.value, setLogicalPorts)} className="input-base w-full text-sm py-1" required>
                      <option value="Routed">Routed</option>
                      <option value="Bridged">Bridged</option>
                      <option value="Internal">Internal</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Role *</label>
                    <select value={port.role} onChange={(e) => updateList(logicalPorts, index, 'role', e.target.value, setLogicalPorts)} className="input-base w-full text-sm py-1" required>
                      <option value="WAN">WAN</option>
                      <option value="LAN">LAN</option>
                      <option value="Loopback">Loopback</option>
                      <option value="MGMT">MGMT</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Physical Port</label>
                    <select value={port.port_id || ''} onChange={(e) => updateList(logicalPorts, index, 'port_id', e.target.value, setLogicalPorts)} className="input-base w-full text-sm py-1">
                      <option value="">Select Port (Optional)</option>
                      {physicalPorts.map(p => <option key={p.port_name} value={p.port_name}>{p.port_name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">VLAN ID</label>
                    <input type="number" value={port.vlan_id || ''} onChange={(e) => updateList(logicalPorts, index, 'vlan_id', e.target.value ? parseInt(e.target.value) : undefined, setLogicalPorts)} className="input-base w-full text-sm py-1" placeholder="1-4094 (for VLAN interfaces)" min="1" max="4094" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">IPv4/CIDR</label>
                    <input type="text" value={port.ipv4 || ''} onChange={(e) => updateList(logicalPorts, index, 'ipv4', e.target.value, setLogicalPorts)} className="input-base w-full text-sm py-1" placeholder="e.g. 192.168.1.1/24 or leave empty for DHCP" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Gateway</label>
                    <input type="text" value={port.gateway || ''} onChange={(e) => updateList(logicalPorts, index, 'gateway', e.target.value, setLogicalPorts)} className="input-base w-full text-sm py-1" placeholder="e.g. 192.168.1.254" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">MTU</label>
                    <input type="number" value={port.mtu || 1500} onChange={(e) => updateList(logicalPorts, index, 'mtu', parseInt(e.target.value) || 1500, setLogicalPorts)} className="input-base w-full text-sm py-1" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">MSS</label>
                    <input type="text" value={port.mss || ''} onChange={(e) => updateList(logicalPorts, index, 'mss', e.target.value, setLogicalPorts)} className="input-base w-full text-sm py-1" placeholder="e.g. 1046" />
                  </div>
                  <div className="flex items-center">
                    <input type="checkbox" checked={port.dhcp_client || false} onChange={(e) => updateList(logicalPorts, index, 'dhcp_client', e.target.checked, setLogicalPorts)} className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
                    <label className="ml-2 block text-sm text-gray-700">DHCP Client</label>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
                    <textarea value={port.description || ''} onChange={(e) => updateList(logicalPorts, index, 'description', e.target.value, setLogicalPorts)} className="input-base w-full text-sm py-1" rows={2} placeholder="Optional description" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Static Routes */}
        <section className="card">
          <div className="flex items-center justify-between mb-4 border-b pb-2">
            <h2 className="text-lg font-semibold text-gray-900">Static Routes</h2>
            <button type="button" onClick={addStaticRoute} className="text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center">
              <Plus className="w-4 h-4 mr-1" /> Add Route
            </button>
          </div>
          <div className="space-y-4">
            {staticRoutes.length === 0 && <p className="text-gray-500 text-sm italic text-center">No static routes defined.</p>}
            {staticRoutes.map((route, index) => (
              <div key={index} className="border border-gray-200 rounded-md p-4 bg-gray-50 relative">
                <button type="button" onClick={() => removeListItem(staticRoutes, index, setStaticRoutes)} className="absolute top-2 right-2 text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Destination IP</label>
                    <input type="text" value={route.target_ip} onChange={(e) => updateList(staticRoutes, index, 'target_ip', e.target.value, setStaticRoutes)} className="input-base w-full text-sm py-1" placeholder="e.g. 10.0.0.0" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Mask (CIDR)</label>
                    <input type="text" value={route.ip_mask} onChange={(e) => updateList(staticRoutes, index, 'ip_mask', e.target.value, setStaticRoutes)} className="input-base w-full text-sm py-1" placeholder="e.g. 24" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Next Hop IP</label>
                    <input type="text" value={route.next_hop_ip} onChange={(e) => updateList(staticRoutes, index, 'next_hop_ip', e.target.value, setStaticRoutes)} className="input-base w-full text-sm py-1" placeholder="e.g. 192.168.1.254" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Interface</label>
                    <select value={route.next_hop_port_id} onChange={(e) => updateList(staticRoutes, index, 'next_hop_port_id', e.target.value, setStaticRoutes)} className="input-base w-full text-sm py-1">
                      <option value="">Select Interface</option>
                      {logicalPorts.map(p => <option key={p.logical_name} value={p.logical_name}>{p.logical_name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Metric</label>
                    <input type="number" value={route.metric} onChange={(e) => updateList(staticRoutes, index, 'metric', parseInt(e.target.value), setStaticRoutes)} className="input-base w-full text-sm py-1" />
                  </div>
                  <div className="flex items-center mt-6">
                    <input type="checkbox" checked={route.enabled} onChange={(e) => updateList(staticRoutes, index, 'enabled', e.target.checked, setStaticRoutes)} className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
                    <label className="ml-2 block text-sm text-gray-700">Enabled</label>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* SNAT Rules */}
        <section className="card">
          <div className="flex items-center justify-between mb-4 border-b pb-2">
            <h2 className="text-lg font-semibold text-gray-900">Source NAT (SNAT)</h2>
            <button type="button" onClick={addSnatRule} className="text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center">
              <Plus className="w-4 h-4 mr-1" /> Add Rule
            </button>
          </div>
          <div className="space-y-4">
            {snatRules.length === 0 && <p className="text-gray-500 text-sm italic text-center">No SNAT rules defined.</p>}
            {snatRules.map((rule, index) => (
              <div key={index} className="border border-gray-200 rounded-md p-4 bg-gray-50 relative">
                <button type="button" onClick={() => removeListItem(snatRules, index, setSnatRules)} className="absolute top-2 right-2 text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Name *</label>
                    <input type="text" value={rule.name} onChange={(e) => updateList(snatRules, index, 'name', e.target.value, setSnatRules)} className="input-base w-full text-sm py-1" required />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Type *</label>
                    <select value={rule.type || 'Masquerading'} onChange={(e) => updateList(snatRules, index, 'type', e.target.value, setSnatRules)} className="input-base w-full text-sm py-1" required>
                      <option value="Masquerading">Masquerading</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Source Network (In IP)</label>
                    <input type="text" value={rule.in_ip || ''} onChange={(e) => updateList(snatRules, index, 'in_ip', e.target.value, setSnatRules)} className="input-base w-full text-sm py-1" placeholder="e.g. 192.168.1.0/24 (optional)" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Translated IP (Out IP)</label>
                    <input type="text" value={rule.out_ip || ''} onChange={(e) => updateList(snatRules, index, 'out_ip', e.target.value, setSnatRules)} className="input-base w-full text-sm py-1" placeholder="e.g. 1.2.3.4 or leave empty for masquerading" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Outgoing Interface *</label>
                    <select value={rule.out_interface_name || ''} onChange={(e) => updateList(snatRules, index, 'out_interface_name', e.target.value, setSnatRules)} className="input-base w-full text-sm py-1" required>
                      <option value="">Select Interface</option>
                      {logicalPorts.map(p => <option key={p.logical_name} value={p.logical_name}>{p.logical_name}</option>)}
                    </select>
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
                    <input type="text" value={rule.description || ''} onChange={(e) => updateList(snatRules, index, 'description', e.target.value, setSnatRules)} className="input-base w-full text-sm py-1" placeholder="Optional description" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* DNAT Rules */}
        <section className="card">
          <div className="flex items-center justify-between mb-4 border-b pb-2">
            <h2 className="text-lg font-semibold text-gray-900">Destination NAT (DNAT)</h2>
            <button type="button" onClick={addDnatRule} className="text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center">
              <Plus className="w-4 h-4 mr-1" /> Add Rule
            </button>
          </div>
          <div className="space-y-4">
            {dnatRules.length === 0 && <p className="text-gray-500 text-sm italic text-center">No DNAT rules defined.</p>}
            {dnatRules.map((rule, index) => (
              <div key={index} className="border border-gray-200 rounded-md p-4 bg-gray-50 relative">
                <button type="button" onClick={() => removeListItem(dnatRules, index, setDnatRules)} className="absolute top-2 right-2 text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
                    <input type="text" value={rule.name} onChange={(e) => updateList(dnatRules, index, 'name', e.target.value, setDnatRules)} className="input-base w-full text-sm py-1" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Public IP (Out IP)</label>
                    <input type="text" value={rule.out_ip} onChange={(e) => updateList(dnatRules, index, 'out_ip', e.target.value, setDnatRules)} className="input-base w-full text-sm py-1" placeholder="e.g. 1.2.3.4" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Public Port</label>
                    <input type="text" value={rule.out_port} onChange={(e) => updateList(dnatRules, index, 'out_port', e.target.value, setDnatRules)} className="input-base w-full text-sm py-1" placeholder="e.g. 80" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Private IP (Local IP)</label>
                    <input type="text" value={rule.local_ip} onChange={(e) => updateList(dnatRules, index, 'local_ip', e.target.value, setDnatRules)} className="input-base w-full text-sm py-1" placeholder="e.g. 192.168.1.100" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Private Port</label>
                    <input type="text" value={rule.local_port} onChange={(e) => updateList(dnatRules, index, 'local_port', e.target.value, setDnatRules)} className="input-base w-full text-sm py-1" placeholder="e.g. 80" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Interface</label>
                    <select value={rule.out_interface} onChange={(e) => updateList(dnatRules, index, 'out_interface', e.target.value, setDnatRules)} className="input-base w-full text-sm py-1">
                      <option value="">Select Interface</option>
                      {logicalPorts.map(p => <option key={p.logical_name} value={p.logical_name}>{p.logical_name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Protocol</label>
                    <select value={rule.protocol} onChange={(e) => updateList(dnatRules, index, 'protocol', e.target.value, setDnatRules)} className="input-base w-full text-sm py-1">
                      <option value="TCP">TCP</option>
                      <option value="UDP">UDP</option>
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* IoT Gateway (wg0) */}
        <section className="card">
          <div className="flex items-center justify-between mb-4 border-b pb-2">
            <h2 className="text-lg font-semibold text-gray-900">IoT Gateway (wg0)</h2>
            <div className="flex items-center">
              <input type="checkbox" checked={wg0Config.enabled} onChange={(e) => setWg0Config({ ...wg0Config, enabled: e.target.checked })} className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
              <label className="ml-2 block text-sm text-gray-900">Enable</label>
            </div>
          </div>
          {wg0Config.enabled && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Listen Port</label>
                  <input type="number" value={wg0Config.listen_port} onChange={(e) => setWg0Config({ ...wg0Config, listen_port: parseInt(e.target.value) || 51820 })} className="input-base w-full text-sm py-1" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Server Address (CIDR)</label>
                  <input type="text" value={wg0Config.address} onChange={(e) => setWg0Config({ ...wg0Config, address: e.target.value })} className="input-base w-full text-sm py-1" placeholder="10.200.0.1/24" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">MTU</label>
                  <input type="number" value={wg0Config.mtu} onChange={(e) => setWg0Config({ ...wg0Config, mtu: parseInt(e.target.value) || 1420 })} className="input-base w-full text-sm py-1" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3">Peers are managed per-edge after provisioning.</p>
            </>
          )}
        </section>

        {/* App VPN (wg1) */}
        <section className="card">
          <div className="flex items-center justify-between mb-4 border-b pb-2">
            <h2 className="text-lg font-semibold text-gray-900">App VPN (wg1)</h2>
            <div className="flex items-center">
              <input type="checkbox" checked={wg1Config.enabled} onChange={(e) => setWg1Config({ ...wg1Config, enabled: e.target.checked })} className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
              <label className="ml-2 block text-sm text-gray-900">Enable</label>
            </div>
          </div>
          {wg1Config.enabled && logicalPorts.some(p => p.role === 'WAN' && p.dhcp_client) && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <strong>Note:</strong> The WAN interface uses DHCP. The App VPN server will be provisioned but won't have a static endpoint until the edge obtains an IP. Clients may need to reconnect if the edge IP changes.
            </div>
          )}
          {wg1Config.enabled && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Tunnel Protocol</label>
                  <select
                    value={wg1Config.tunnel_protocol}
                    onChange={(e) => {
                      const proto = e.target.value as 'wireguard' | 'ikev2';
                      setWg1Config({
                        ...wg1Config,
                        tunnel_protocol: proto,
                        address: proto === 'ikev2' ? '10.11.0.0/16' : '10.10.0.254/16',
                        listen_port: proto === 'ikev2' ? 0 : 51821,
                      });
                    }}
                    className="input-base w-full text-sm py-1"
                  >
                    <option value="wireguard">WireGuard</option>
                    <option value="ikev2">IKEv2</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    {wg1Config.tunnel_protocol === 'ikev2' ? 'Listen Port (N/A)' : 'Listen Port'}
                  </label>
                  <input
                    type="number"
                    value={wg1Config.tunnel_protocol === 'ikev2' ? '' : wg1Config.listen_port}
                    onChange={(e) => setWg1Config({ ...wg1Config, listen_port: parseInt(e.target.value) || 51821 })}
                    className="input-base w-full text-sm py-1"
                    disabled={wg1Config.tunnel_protocol === 'ikev2'}
                    placeholder={wg1Config.tunnel_protocol === 'ikev2' ? 'N/A' : ''}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Server Address (CIDR)</label>
                  <input type="text" value={wg1Config.address} onChange={(e) => setWg1Config({ ...wg1Config, address: e.target.value })} className="input-base w-full text-sm py-1" placeholder={wg1Config.tunnel_protocol === 'ikev2' ? '10.11.0.0/16' : '10.10.0.254/16'} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">DNS Server</label>
                  <input type="text" value={wg1Config.dns} onChange={(e) => setWg1Config({ ...wg1Config, dns: e.target.value })} className="input-base w-full text-sm py-1" placeholder="8.8.8.8" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">MTU</label>
                  <input type="number" value={wg1Config.mtu} onChange={(e) => setWg1Config({ ...wg1Config, mtu: parseInt(e.target.value) || 1420 })} className="input-base w-full text-sm py-1" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3">Client peers provisioned via App VPN user management.</p>
            </>
          )}
        </section>

        {/* SSH Remote Access */}
        <section className="card">
          <div className="flex items-center justify-between mb-4 border-b pb-2">
            <h2 className="text-lg font-semibold text-gray-900">SSH Remote Access</h2>
            <div className="flex items-center">
              <input type="checkbox" checked={sshTunnel.enabled} onChange={(e) => setSshTunnel({ enabled: e.target.checked })} className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
              <label className="ml-2 block text-sm text-gray-900">Enable</label>
            </div>
          </div>
          <p className="text-sm text-gray-600">Automatically provision SSH reverse tunnel for remote management. Port allocated per-edge.</p>
        </section>

        {/* IPFIX / Flowprobe */}
        <section className="card">
          <div className="flex items-center justify-between mb-4 border-b pb-2">
            <h2 className="text-lg font-semibold text-gray-900">IPFIX / Flowprobe</h2>
            <div className="flex items-center">
              <input type="checkbox" checked={flowprobeConfig.enabled} onChange={(e) => setFlowprobeConfig({ ...flowprobeConfig, enabled: e.target.checked })} className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
              <label className="ml-2 block text-sm text-gray-900">Enable</label>
            </div>
          </div>
          {flowprobeConfig.enabled && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Collector Address</label>
                  <input type="text" value={flowprobeConfig.collector_address} onChange={(e) => setFlowprobeConfig({ ...flowprobeConfig, collector_address: e.target.value })} className="input-base w-full text-sm py-1" placeholder="169.254.1.2" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Collector Port</label>
                  <input type="number" value={flowprobeConfig.collector_port} onChange={(e) => setFlowprobeConfig({ ...flowprobeConfig, collector_port: parseInt(e.target.value) || 4739 })} className="input-base w-full text-sm py-1" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Template Interval (sec)</label>
                  <input type="number" value={flowprobeConfig.template_interval} onChange={(e) => setFlowprobeConfig({ ...flowprobeConfig, template_interval: parseInt(e.target.value) || 20 })} className="input-base w-full text-sm py-1" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Active Timer (sec)</label>
                  <input type="number" value={flowprobeConfig.active_timer} onChange={(e) => setFlowprobeConfig({ ...flowprobeConfig, active_timer: parseInt(e.target.value) || 60 })} className="input-base w-full text-sm py-1" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Passive Timer (sec)</label>
                  <input type="number" value={flowprobeConfig.passive_timer} onChange={(e) => setFlowprobeConfig({ ...flowprobeConfig, passive_timer: parseInt(e.target.value) || 120 })} className="input-base w-full text-sm py-1" />
                </div>
              </div>
              <div className="flex items-center space-x-6">
                <div className="flex items-center">
                  <input type="checkbox" checked={flowprobeConfig.record_l2} onChange={(e) => setFlowprobeConfig({ ...flowprobeConfig, record_l2: e.target.checked })} className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
                  <label className="ml-2 block text-sm text-gray-700">Record L2</label>
                </div>
                <div className="flex items-center">
                  <input type="checkbox" checked={flowprobeConfig.record_l3} onChange={(e) => setFlowprobeConfig({ ...flowprobeConfig, record_l3: e.target.checked })} className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
                  <label className="ml-2 block text-sm text-gray-700">Record L3</label>
                </div>
                <div className="flex items-center">
                  <input type="checkbox" checked={flowprobeConfig.record_l4} onChange={(e) => setFlowprobeConfig({ ...flowprobeConfig, record_l4: e.target.checked })} className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
                  <label className="ml-2 block text-sm text-gray-700">Record L4</label>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3">Monitored interfaces configured per-edge after provisioning.</p>
            </>
          )}
        </section>

        {/* DHCP Pools */}
        <section className="card">
          <div className="flex items-center justify-between mb-4 border-b pb-2">
            <h2 className="text-lg font-semibold text-gray-900">DHCP Pools</h2>
            <button type="button" onClick={addDhcpPool} className="text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center">
              <Plus className="w-4 h-4 mr-1" /> Add Pool
            </button>
          </div>
          <div className="space-y-4">
            {dhcpPools.length === 0 && <p className="text-gray-500 text-sm italic text-center">No DHCP pools defined.</p>}
            {dhcpPools.map((pool, index) => (
              <div key={index} className="border border-gray-200 rounded-md p-4 bg-gray-50 relative">
                <button type="button" onClick={() => removeListItem(dhcpPools, index, setDhcpPools)} className="absolute top-2 right-2 text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Pool Name *</label>
                    <input type="text" value={pool.name} onChange={(e) => updateList(dhcpPools, index, 'name', e.target.value, setDhcpPools)} className="input-base w-full text-sm py-1" required />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Interface *</label>
                    <select value={pool.interface_name} onChange={(e) => updateList(dhcpPools, index, 'interface_name', e.target.value, setDhcpPools)} className="input-base w-full text-sm py-1" required>
                      <option value="">Select Interface</option>
                      {logicalPorts.map(p => <option key={p.logical_name} value={p.logical_name}>{p.logical_name}</option>)}
                    </select>
                  </div>
                  <div className="flex items-center mt-5">
                    <input type="checkbox" checked={pool.enabled} onChange={(e) => updateList(dhcpPools, index, 'enabled', e.target.checked, setDhcpPools)} className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
                    <label className="ml-2 block text-sm text-gray-700">Enabled</label>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Start IP *</label>
                    <input type="text" value={pool.start_ip} onChange={(e) => updateList(dhcpPools, index, 'start_ip', e.target.value, setDhcpPools)} className="input-base w-full text-sm py-1" placeholder="e.g. 192.168.1.100" required />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">End IP *</label>
                    <input type="text" value={pool.end_ip} onChange={(e) => updateList(dhcpPools, index, 'end_ip', e.target.value, setDhcpPools)} className="input-base w-full text-sm py-1" placeholder="e.g. 192.168.1.200" required />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Gateway</label>
                    <input type="text" value={pool.gateway} onChange={(e) => updateList(dhcpPools, index, 'gateway', e.target.value, setDhcpPools)} className="input-base w-full text-sm py-1" placeholder="e.g. 192.168.1.1" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">DNS Servers</label>
                    <input type="text" value={pool.dns_servers} onChange={(e) => updateList(dhcpPools, index, 'dns_servers', e.target.value, setDhcpPools)} className="input-base w-full text-sm py-1" placeholder="e.g. 8.8.8.8,8.8.4.4" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Lease Time (seconds)</label>
                    <input type="number" value={pool.lease_time} onChange={(e) => updateList(dhcpPools, index, 'lease_time', parseInt(e.target.value) || 86400, setDhcpPools)} className="input-base w-full text-sm py-1" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Advanced Configuration Banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Info className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-semibold text-amber-900">Advanced Per-Edge Configuration</h3>
              <p className="text-sm text-amber-700 mt-1">
                The following features are configured per-edge after provisioning and are not
                included in templates:
              </p>
              <ul className="text-sm text-amber-700 mt-2 list-disc list-inside space-y-1">
                <li>Suricata IDS/IPS and Service Chaining</li>
                <li>Access Control Lists (ACLs)</li>
                <li>QoS and Traffic Policers</li>
                <li>E2E Peering Tunnels</li>
                <li>WireGuard / App VPN Peers</li>
              </ul>
            </div>
          </div>
        </div>

      </form>
    </div>
  );
};

export default ConfigTemplateDetail;
